import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["form", "payload", "submit", "cancel", "status", "output", "metadata", "requestLink"];

  disconnect() { this.abortController?.abort(); }

  changeModel(event) {
    try {
      const data = JSON.parse(this.payloadTarget.value);
      data.model = event.target.value;
      this.payloadTarget.value = JSON.stringify(data, null, 2);
    } catch (_) {
      this.statusTarget.textContent = "请先修正请求 JSON";
    }
  }

  async run(event) {
    event.preventDefault();
    if (this.abortController) return;
    try { JSON.parse(this.payloadTarget.value); } catch (_) {
      this.statusTarget.textContent = "JSON 格式无效";
      return;
    }
    this.abortController = new AbortController();
    this.submitTarget.disabled = true;
    this.cancelTarget.disabled = false;
    this.outputTarget.textContent = "";
    this.metadataTarget.textContent = "";
    this.requestLinkTarget.classList.add("hidden");
    this.statusTarget.textContent = "正在调用…";
    try {
      const headers = { "Accept": "application/json" };
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
      if (csrfToken) headers["X-CSRF-Token"] = csrfToken;
      const response = await fetch(this.formTarget.action, {
        method: "POST", body: new FormData(this.formTarget), credentials: "same-origin",
        headers,
        signal: this.abortController.signal,
      });
      if (response.redirected) throw new Error("登录已过期，请刷新页面。");
      if (!response.ok) {
        const data = await response.json();
        this.linkRequest(data.request_id);
        throw new Error(data.error?.message || "请求失败");
      }
      if (response.headers.get("Content-Type")?.includes("text/event-stream")) {
        this.linkRequest(response.headers.get("X-Request-ID"));
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "", completed = false;
        while (true) {
          const { value, done } = await reader.read();
          buffer += decoder.decode(value || new Uint8Array(), { stream: !done });
          let boundary;
          while ((boundary = buffer.indexOf("\n\n")) !== -1) {
            const frame = buffer.slice(0, boundary);
            buffer = buffer.slice(boundary + 2);
            if (!frame.startsWith("data: ")) continue;
            const payload = frame.slice(6);
            if (payload === "[DONE]") { completed = true; continue; }
            const data = JSON.parse(payload);
            if (data.error) throw new Error(data.error.message);
            for (const choice of data.choices || []) {
              if (choice.delta?.content) this.outputTarget.textContent += choice.delta.content;
              if (choice.delta?.tool_calls) this.outputTarget.textContent += `\n${JSON.stringify(choice.delta.tool_calls)}`;
            }
            if (data.usage) this.metadataTarget.textContent = JSON.stringify(data.usage, null, 2);
          }
          if (done) break;
        }
        if (!completed) throw new Error("响应意外中断，请查看调用记录。");
      } else {
        const data = await response.json();
        this.outputTarget.textContent = data.response?.choices?.[0]?.message?.content || JSON.stringify(data.response?.choices?.[0]?.message, null, 2);
        this.metadataTarget.textContent = JSON.stringify({ usage: data.response?.usage, cost: data.cost }, null, 2);
        this.linkRequest(data.request_id);
      }
      this.statusTarget.textContent = "调用完成";
    } catch (error) {
      this.statusTarget.textContent = error.name === "AbortError" ? "已停止接收" : "调用失败";
      if (error.name !== "AbortError") this.metadataTarget.textContent = error.message;
    } finally {
      this.abortController = null;
      this.submitTarget.disabled = false;
      this.cancelTarget.disabled = true;
    }
  }

  cancel() { this.abortController?.abort(); }

  linkRequest(id) {
    if (!id || !/^[a-f0-9-]{36}$/.test(id)) return;
    this.requestLinkTarget.href = `/ai/requests/${id}`;
    this.requestLinkTarget.classList.remove("hidden");
  }
}
